/**
 * RENDER — the marble (the visual hero) + its contact shadow.
 *
 * A single chrome sphere lit by the procedural env map; it reflects the neon course
 * and is the most reflective object on screen. We render a cheap blob contact shadow
 * underneath so it stays grounded without a real-time shadow map (mobile budget).
 *
 * The marble's visual SPIN is derived from velocity (a rolling ball turns about the
 * axis perpendicular to its travel). This is purely cosmetic — it never feeds back
 * into the sim — but it sells the "rolling momentum" feel. Position is set by the
 * renderer from the interpolated GameState; we only own appearance + spin + shadow.
 */

import * as THREE from 'three';
import type { Vec3 } from '@/types';
import { PALETTE } from './theme';
import { disposeMaterial } from './three-util';
import { lenVec3 } from './math';

export class Marble {
  /** Group the renderer positions each frame; marble + shadow ride together. */
  readonly group: THREE.Group;

  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.SphereGeometry;
  private readonly material: THREE.MeshStandardMaterial;

  private readonly shadow: THREE.Mesh;
  private readonly shadowGeo: THREE.CircleGeometry;
  private readonly shadowMat: THREE.MeshBasicMaterial;
  private readonly shadowTex: THREE.Texture;

  private readonly radius: number;

  /** Accumulated roll, applied as a quaternion each frame. Scratch reused. */
  private readonly rollAxis = new THREE.Vector3(1, 0, 0);
  private readonly rollQuat = new THREE.Quaternion();
  private readonly spinQuat = new THREE.Quaternion();
  private readonly worldUp = new THREE.Vector3(0, 1, 0);

  constructor(radius: number) {
    this.radius = radius;
    this.group = new THREE.Group();
    this.group.name = 'marble';

    this.geometry = new THREE.SphereGeometry(radius, 32, 24);
    this.material = new THREE.MeshStandardMaterial({
      color: 0xcdd6e6,
      metalness: 1.0,
      roughness: 0.12,
      envMapIntensity: 1.25,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = 'marble-mesh';
    this.group.add(this.mesh);

    // Soft radial-gradient blob shadow on a flat disc just under the marble.
    this.shadowTex = makeBlobTexture();
    this.shadowGeo = new THREE.CircleGeometry(radius * 2.4, 24);
    this.shadowMat = new THREE.MeshBasicMaterial({
      map: this.shadowTex,
      transparent: true,
      depthWrite: false,
      opacity: 0.55,
      color: PALETTE.void,
    });
    this.shadow = new THREE.Mesh(this.shadowGeo, this.shadowMat);
    this.shadow.name = 'marble-shadow';
    this.shadow.rotation.x = -Math.PI / 2; // lie flat on the ground plane
    this.shadow.position.y = -radius + 0.02;
    this.group.add(this.shadow);
  }

  /**
   * Set the marble's world position and update its cosmetic roll from velocity.
   * `dt` is the render frame delta (seconds). Allocation-free.
   */
  update(position: Vec3, velocity: Vec3, dt: number): void {
    this.group.position.set(position[0], position[1], position[2]);

    const speed = lenVec3(velocity);
    if (speed > 1e-4 && dt > 0) {
      // Roll axis = up × velocityDir (a ball rolling forward spins about its side axis).
      this.rollAxis.set(velocity[0], 0, velocity[2]);
      if (this.rollAxis.lengthSq() < 1e-8) {
        // Purely vertical motion: nothing meaningful to roll about.
        return;
      }
      this.rollAxis.normalize();
      this.rollAxis.cross(this.worldUp); // perpendicular roll axis
      this.rollAxis.normalize();
      // Arc length / radius = angle rolled this frame.
      const angle = (speed * dt) / this.radius;
      this.rollQuat.setFromAxisAngle(this.rollAxis, -angle);
      this.spinQuat.multiplyQuaternions(this.rollQuat, this.spinQuat).normalize();
      this.mesh.quaternion.copy(this.spinQuat);
    }
  }

  /** Make the marble flash bright briefly (e.g. near-miss) — emissive pulse. */
  setEmissivePulse(color: number, intensity: number): void {
    this.material.emissive.setHex(color);
    this.material.emissiveIntensity = intensity;
  }

  dispose(): void {
    this.geometry.dispose();
    disposeMaterial(this.material);
    this.shadowGeo.dispose();
    // disposeMaterial frees the material's `map` (== shadowTex) too.
    disposeMaterial(this.shadowMat);
    this.group.removeFromParent();
    this.group.clear();
  }
}

/** A soft radial alpha falloff for the contact shadow. Built once, 64×64. */
function makeBlobTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    const tex = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    tex.needsUpdate = true;
    return tex;
  }
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}
